import axiosInstance from '../auth/axiosInstance';
import { invalidateAfterAssessmentChange } from '../utils/clientProjectDataCache';

export const getAssessmentDetails = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post(
      '/Capability/get-selected-capabilities',
      payload
    );
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    console.error('Error during API call:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const submitAssessment = async (payload) => {
  try {
    const transformedPayload = {
      id: 0,
      userId: payload.userId,
      projectId: payload.projectId,
      subCapabilitiesAssessments: payload.subCapabilitiesAssessments.map((subCap) => ({
        capabilityId: subCap.capabilityId,
        subCapabilityId: subCap.subCapabilityId,
        currentStatusDescription: subCap.currentStatusDescription,
        futureStatusDescription: subCap.futureStatusDescription,
        currentStatusRating: subCap.currentStatusRating,
        futureStatusRating: subCap.futureStatusRating,
        businessValue: subCap.businessValue || '',
        businessValueImpact: subCap.businessValueImpact || 0,
      })),
    };

    const { data } = await axiosInstance.post(
      '/Assessment/add-or-update-sub-capability-assessment',
      transformedPayload
    );
    if (payload?.userId != null && payload?.projectId != null) {
      invalidateAfterAssessmentChange(payload.userId, payload.projectId);
    }
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    throw new Error(errorMessage);
  }
};

export const getSubCapabilityAssessmentData = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post(
      '/Assessment/get-sub-capabilities-assessment-details',
      payload
    );
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    throw new Error(errorMessage);
  }
};

export const submitSMEState = async (payload) => {
  try {
    const { data } = await axiosInstance.post(
      '/Capability/add-or-update-sme-submit-state',
      payload
    );
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    console.error('Error during API call:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const fetchRequestStatus = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post(
      '/Capability/get-capability-request-details',
      payload
    );
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    console.error('Error during API call:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const SubmitCapabilityRequest = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post('/Capability/add-capability-request', payload);
    return data;
  } catch (error) {
    const errorMessage = error.response
      ? error.response.data.message || error.message
      : error.message;

    throw new Error(errorMessage);
  }
};

export const fetchAssessmentSubmitMemberCount = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post('/Assessment/get-assess-resp-count', payload);
    return data;
  } catch (error) {
    console.error('Error fetching capabilities:', error);
    throw error;
  }
};

const EXCEL_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const readBlobErrorMessage = async (data) => {
  if (!data) {
    return 'Failed to export workbook.';
  }

  if (typeof data.text !== 'function') {
    return 'Failed to export workbook.';
  }

  const text = await data.text();
  try {
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.title || text || 'Failed to export workbook.';
  } catch {
    return text || 'Failed to export workbook.';
  }
};

export const exportAssessmentWorkbook = async (payload) => {
  try {
    const response = await axiosInstance.post('/Assessment/export-workbook', payload, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json') || contentType.includes('text/plain')) {
      throw new Error(await readBlobErrorMessage(response.data));
    }

    const disposition = response.headers['content-disposition'] || '';
    const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : `assessment-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const blob = new Blob([response.data], { type: EXCEL_CONTENT_TYPE });

    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.remove();
  } catch (error) {
    const blobData = error?.response?.data;
    if (blobData instanceof Blob) {
      throw new Error(await readBlobErrorMessage(blobData));
    }

    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to export workbook.';
    throw new Error(errorMessage);
  }
};

export const importAssessmentWorkbook = async ({ userId, projectId, file }) => {
  if (!file) {
    throw new Error('Please choose an Excel file to import.');
  }

  const formData = new FormData();
  formData.append('userId', String(userId));
  formData.append('projectId', String(projectId));
  formData.append('file', file);

  const { data } = await axiosInstance.post('/Assessment/import-workbook', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const payload = data?.data ?? data;
  const errors = payload?.errors ?? payload?.Errors ?? [];
  const importedCount = payload?.importedCount ?? payload?.ImportedCount ?? 0;
  const failed = Array.isArray(errors) && errors.length > 0
    ? true
    : data?.succeeded === false && importedCount <= 0;

  if (failed) {
    throw new Error(data?.message || 'We could not import this workbook.');
  }

  return data;
};
